import { Box, Button, RadioGroup } from '@wix/design-system'
import { ChoiceQuestionContentComponent, useContent, useNext } from '@wix/funnel-builder-lizard'
import classNames from 'classnames'
import React, { FC, useEffect, useState } from 'react'

import { usePageLogger } from '../../../bi/mfi'
import { useBrowserTabTitle } from '../../../hooks/useBrowserTabTitle'
import { Heading } from '../../reusable/Heading/Heading'
import { WixIntrosTemplate } from '../../reusable/WixIntrosTemplate'
import { wixIntrosRadiogroupDataHooks as dataHooks } from './WixIntrosRadiogroup.dataHooks'
import s from './WixIntrosRadiogroup.scss'

export type Content = {
  browserTabTitle: string
  subTitleAbove: string
  skip: string
  question: ChoiceQuestionContentComponent<{ title: string; button: string }, { label: string }>
}

export const WixIntrosRadiogroup: FC = () => {
  const [selected, setSelected] = useState<string | number | undefined>(undefined)
  const { content } = useContent<Content>()
  const { questionKey, options, content: questionContent } = content.question

  useBrowserTabTitle(content.browserTabTitle)
  const pageLogger = usePageLogger()
  const next = useNext()

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  const onSubmit = () => {
    void pageLogger.answer({ answer: selected as string })
    next({ [questionKey]: { values: [selected as string] } })
  }

  const onSkip = () => {
    void pageLogger.skip()
    next({ [questionKey]: { skipped: true } })
  }

  return (
    <WixIntrosTemplate withLogo skip={content.skip} onSkip={onSkip}>
      <Box marginBottom="SP2">
        <Heading weight="normal" size="large" dataHook={dataHooks.subtitle()}>
          {content.subTitleAbove}
        </Heading>
      </Box>
      <Box marginBottom="SP9">
        <Heading size="xxl" dataHook={dataHooks.title()}>
          {questionContent.title}
        </Heading>
      </Box>
      <Box maxWidth="450px" width="100%" marginBottom="SP9">
        <RadioGroup
          dataHook={dataHooks.radioGroup()}
          spacing="24px"
          selectionArea="always"
          selectionAreaSkin="outlined"
          value={selected}
          onChange={setSelected}
          className={s.WixIntrosRadiogroup__radioGroup}
          selectionAreaPadding="20px 24px"
        >
          {options.map(option => (
            <RadioGroup.Radio key={option.optionKey} value={option.optionKey}>
              {option.label}
            </RadioGroup.Radio>
          ))}
        </RadioGroup>
      </Box>
      <Box
        className={classNames(s.WixIntrosRadiogroup__buttonContainer, {
          [s.WixIntrosRadiogroup__buttonContainerHidden]: !selected,
        })}
      >
        <Button size="large" onClick={onSubmit} dataHook={dataHooks.submitButton()} disabled={!selected}>
          {questionContent.button}
        </Button>
      </Box>
    </WixIntrosTemplate>
  )
}
